import React, { useState, useRef, useEffect } from 'react';

/**
 * Composant OTPInput - Saisie de code OTP à 6 chiffres
 *
 * Fonctionnalités:
 * - 6 champs pour les 6 chiffres
 * - Navigation automatique entre les champs
 * - Support du copier-coller
 * - Support de la suppression (backspace)
 * - Validation numérique
 * - Design bleu/orange
 *
 * @param {number} length - Longueur du code OTP (défaut: 6)
 * @param {function} onComplete - Callback appelé quand le code est complet
 * @param {boolean} disabled - Désactiver les inputs
 */
const OTPInput = ({ length = 6, onComplete, disabled = false }) => {
  const [otp, setOtp] = useState(new Array(length).fill(''));
  const inputRefs = useRef([]);

  // Initialiser les refs pour chaque input
  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, length);
  }, [length]);

  // Focus sur le premier input au montage
  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  /**
   * Gestion du changement de valeur dans un input
   */
  const handleChange = (index, value) => {
    // Accepter uniquement les chiffres
    if (value && !/^\d+$/.test(value)) {
      return;
    }

    const newOtp = [...otp];

    // Si on colle plusieurs chiffres
    if (value.length > 1) {
      const pastedData = value.slice(0, length).split('');
      pastedData.forEach((char, i) => {
        if (index + i < length && /^\d$/.test(char)) {
          newOtp[index + i] = char;
        }
      });

      setOtp(newOtp);

      // Focus sur le dernier input rempli ou le suivant
      const nextIndex = Math.min(index + pastedData.length, length - 1);
      inputRefs.current[nextIndex]?.focus();

      // Vérifier si le code est complet
      if (newOtp.every(digit => digit !== '')) {
        onComplete(newOtp.join(''));
      }

      return;
    }

    // Changement d'un seul chiffre
    newOtp[index] = value;
    setOtp(newOtp);

    // Passer au champ suivant si on a saisi un chiffre
    if (value && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    // Vérifier si le code est complet
    if (newOtp.every(digit => digit !== '')) {
      onComplete(newOtp.join(''));
    }
  };

  /**
   * Gestion de la touche appuyée
   */
  const handleKeyDown = (index, e) => {
    // Backspace: supprimer et revenir au champ précédent
    if (e.key === 'Backspace') {
      e.preventDefault();

      const newOtp = [...otp];

      if (otp[index]) {
        // Si le champ actuel a une valeur, la supprimer
        newOtp[index] = '';
        setOtp(newOtp);
      } else if (index > 0) {
        // Sinon, revenir au champ précédent et le vider
        newOtp[index - 1] = '';
        setOtp(newOtp);
        inputRefs.current[index - 1]?.focus();
      }
    }

    // ArrowLeft: aller au champ précédent
    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    // ArrowRight: aller au champ suivant
    if (e.key === 'ArrowRight' && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  /**
   * Gestion du focus: sélectionner tout le contenu
   */
  const handleFocus = (index) => {
    inputRefs.current[index]?.select();
  };

  /**
   * Gestion du copier-coller
   */
  const handlePaste = (e) => {
    e.preventDefault();

    const pastedData = e.clipboardData.getData('text/plain').trim();

    // Vérifier que ce sont uniquement des chiffres
    if (!/^\d+$/.test(pastedData)) {
      return;
    }

    const digits = pastedData.slice(0, length).split('');
    const newOtp = [...otp];

    digits.forEach((digit, i) => {
      if (i < length) {
        newOtp[i] = digit;
      }
    });

    setOtp(newOtp);

    // Focus sur le dernier champ rempli
    const lastIndex = Math.min(digits.length, length) - 1;
    inputRefs.current[lastIndex]?.focus();

    // Vérifier si le code est complet
    if (newOtp.every(digit => digit !== '')) {
      onComplete(newOtp.join(''));
    }
  };

  return (
    <div className="space-y-4">
      <label className="block text-sm font-medium text-gray-700 text-center mb-3">
        Code de vérification à 6 chiffres
      </label>

      <div className="flex justify-center gap-2 sm:gap-3">
        {otp.map((digit, index) => (
          <input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onFocus={() => handleFocus(index)}
            onPaste={handlePaste}
            disabled={disabled}
            className={`
              w-12 h-14 sm:w-14 sm:h-16
              text-center text-2xl font-bold
              border-2 rounded-xl
              transition duration-150 ease-in-out
              focus:outline-none focus:ring-2 focus:ring-offset-2
              ${digit
                ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-orange-50 text-gray-800'
                : 'border-gray-300 bg-white text-gray-800'
              }
              ${disabled
                ? 'opacity-50 cursor-not-allowed'
                : 'hover:border-blue-400 focus:border-blue-500 focus:ring-blue-500'
              }
            `}
            aria-label={`Chiffre ${index + 1}`}
          />
        ))}
      </div>

      {/* Instructions */}
      <p className="text-center text-xs text-gray-500 mt-3">
        Saisissez le code reçu par email
      </p>
    </div>
  );
};

export default OTPInput;
